import React, { useState } from 'react';
import { useProperties } from '../hooks/useProperties';
import { useCustomers } from '../../customers/hooks/useCustomers';
import { useNavigate } from 'react-router-dom';
import { Property } from '../types';

export const CreateProperty: React.FC = () => {
  const { addProperty } = useProperties();
  const { customers } = useCustomers();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [newProperty, setNewProperty] = useState<Omit<Property, 'id' | 'createdAt' | 'updatedAt'>>({
    name: '',
    customerId: '',
    propertyType: 'house',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    squareFootage: undefined,
    bedrooms: 0,
    bathrooms: 0,
    floors: 1,
    accessInstructions: '',
    specialInstructions: '',
    hasPets: false,
    petDetails: '',
    preferredCleaningDay: undefined,
    requestedTimeSlot: undefined,
    isActive: true
  });

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setNewProperty(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddProperty = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!newProperty.customerId) errors.customerId = 'Customer is required.';
    if (!newProperty.addressLine1) errors.addressLine1 = 'Address is required.';
    if (!newProperty.city) errors.city = 'City is required.';
    if (!newProperty.state) errors.state = 'State is required.';
    if (!newProperty.postalCode) errors.postalCode = 'Postal code is required.';

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      await addProperty({ ...newProperty, name: newProperty.addressLine1 });
      navigate('/properties');
    } catch (error) {
      console.error('Failed to add property:', error);
      alert(`Error adding property: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Property</h2>
            <form onSubmit={handleAddProperty} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Customer</label>
                    <select
                    required
                    className={`mt-1 block w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 ${formErrors.customerId ? 'border-red-500' : 'border-gray-300'}`}
                    value={newProperty.customerId}
                    onChange={(e) => handleInputChange('customerId', e.target.value)}
                    >
                    <option value="">Select a customer</option>
                    {customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>{customer.name}</option>
                    ))}
                    </select>
                    {formErrors.customerId && <p className="text-red-500 text-xs mt-1">{formErrors.customerId}</p>}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Property Type</label>
                    <select
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    value={newProperty.propertyType}
                    onChange={(e) => handleInputChange('propertyType', e.target.value)}
                    >
                    <option value="house">House</option>
                    <option value="apartment">Apartment</option>
                    <option value="condo">Condo</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="office">Office</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
                    <input
                    type="text"
                    required
                    className={`mt-1 block w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 ${formErrors.addressLine1 ? 'border-red-500' : 'border-gray-300'}`}
                    value={newProperty.addressLine1}
                    onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                    />
                    {formErrors.addressLine1 && <p className="text-red-500 text-xs mt-1">{formErrors.addressLine1}</p>}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Address Line 2 (Optional)</label>
                    <input
                    type="text"
                    placeholder="Apt, Suite, etc."
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    value={newProperty.addressLine2}
                    onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <input
                        type="text"
                        required
                        className={`mt-1 block w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 ${formErrors.city ? 'border-red-500' : 'border-gray-300'}`}
                        value={newProperty.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                    />
                    {formErrors.city && <p className="text-red-500 text-xs mt-1">{formErrors.city}</p>}
                    </div>
                    <div>
                    <label className="block text-sm font-medium text-gray-700">State</label>
                    <input
                        type="text"
                        required
                        className={`mt-1 block w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 ${formErrors.state ? 'border-red-500' : 'border-gray-300'}`}
                        value={newProperty.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                    />
                    {formErrors.state && <p className="text-red-500 text-xs mt-1">{formErrors.state}</p>}
                    </div>
                    <div>
                    <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                    <input
                        type="text"
                        required
                        className={`mt-1 block w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 ${formErrors.postalCode ? 'border-red-500' : 'border-gray-300'}`}
                        value={newProperty.postalCode}
                        onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    />
                    {formErrors.postalCode && <p className="text-red-500 text-xs mt-1">{formErrors.postalCode}</p>}
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                    <label className="block text-sm font-medium text-gray-700">Square Footage</label>
                    <input
                        type="number"
                        min="0"
                        className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        value={newProperty.squareFootage || ''}
                        onChange={(e) => handleInputChange('squareFootage', e.target.value ? parseInt(e.target.value) : 0)}
                    />
                    </div>
                    <div>
                    <label className="block text-sm font-medium text-gray-700">Bedrooms</label>
                    <input
                        type="number"
                        min="0"
                        className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        value={newProperty.bedrooms || 0}
                        onChange={(e) => handleInputChange('bedrooms', parseInt(e.target.value) || 0)}
                    />
                    </div>
                    <div>
                    <label className="block text-sm font-medium text-gray-700">Bathrooms</label>
                    <input
                        type="number"
                        min="0"
                        step="0.5"
                        className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        value={newProperty.bathrooms || 0}
                        onChange={(e) => handleInputChange('bathrooms', parseFloat(e.target.value) || 0)}
                    />
                    </div>
                    <div>
                    <label className="block text-sm font-medium text-gray-700">Floors</label>
                    <input
                        type="number"
                        min="1"
                        className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        value={newProperty.floors || 1}
                        onChange={(e) => handleInputChange('floors', parseInt(e.target.value) || 1)}
                    />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Access Instructions</label>
                    <textarea
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    rows={2}
                    placeholder="Gate codes, key location, entry instructions..."
                    value={newProperty.accessInstructions}
                    onChange={(e) => handleInputChange('accessInstructions', e.target.value)}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Special Instructions</label>
                    <textarea
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    rows={2}
                    placeholder="Special cleaning requirements, preferences..."
                    value={newProperty.specialInstructions}
                    onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                    />
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex items-center">
                    <input
                        id="has-pets"
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        checked={newProperty.hasPets}
                        onChange={(e) => handleInputChange('hasPets', e.target.checked)}
                    />
                    <label htmlFor="has-pets" className="ml-2 block text-sm text-gray-900">
                        Has Pets
                    </label>
                    </div>
                </div>

                {newProperty.hasPets && (
                    <div>
                    <label className="block text-sm font-medium text-gray-700">Pet Details</label>
                    <textarea
                        className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        rows={2}
                        placeholder="Pet types, special considerations..."
                        value={newProperty.petDetails}
                        onChange={(e) => handleInputChange('petDetails', e.target.value)}
                    />
                    </div>
                )}

                <div className="flex space-x-3 pt-4">
                    <button
                    type="submit"
                    className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                    disabled={isSubmitting}
                    >
                    {isSubmitting ? 'Adding...' : 'Add Property'}
                    </button>
                    <button
                    type="button"
                    onClick={() => navigate('/properties')}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                    disabled={isSubmitting}
                    >
                    Cancel
                    </button>
                </div>
            </form>
        </div>
    </div>
    );
};

